import type { ListResponse, ObjectResponse } from '../../../common/types';
import { annotationToolApiV1, getAnnotationToolBasePath } from '../../../lib';
import type { ManageProjectResponse } from '../../admin-projects/types/response';
import { CREATE_PROJECT_WIZARD_TAG } from '../constants';
import { AnnotationUser } from '../state/createProjectSlice';
import { CreateAnnotationProjectRequest } from '../types/request';
import type {
    AllocationSummaryResponse,
    AnnotatorResponse,
    AttributeResponse,
    ConceptGroupResponse,
    ConditionAreaResponse,
    DataSplitSummaryResponse,
    OrganizationResponse,
    SupervisorResponse,
} from '../types/response';

export const createProjectApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [CREATE_PROJECT_WIZARD_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getManageProjects: builder.query<ListResponse<ManageProjectResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return `${tempPath}/manage-projects/projects`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getConditionAreas: builder.query<ListResponse<ConditionAreaResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/conditions`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getOrganizations: builder.query<ListResponse<OrganizationResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/organizations`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getConceptGroups: builder.query<ListResponse<ConceptGroupResponse>, { search?: string }>({
        query: ({ search }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/concept-groups?search=${search}`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getAttributes: builder.query<ListResponse<AttributeResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/attributes`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getMandatoryAttributeNames: builder.query<ListResponse<string>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/mandatory-attribute-names`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      createAnnotationProject: builder.mutation<
        ObjectResponse<{ projectId: string; status: string }>,
        CreateAnnotationProjectRequest
      >({
        query: ({
          basics,
          conceptGroups,
          annotators,
          supervisors,
          defaultAllocationMode,
          workflowId,
        }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/project`,
            method: 'POST',
            body: {
              basics,
              conceptGroups,
              annotators,
              supervisors,
              defaultAllocationMode,
              workflowId,
            },
          };
        },
      }),

      createWorkflowId: builder.mutation<ObjectResponse<string>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/create-workflow-id`,
            method: 'POST',
          };
        },
      }),

      uploadNotesParquetFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/notes-file`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      uploadPersonParquetFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/person-file`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      uploadVisitParquetFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/visit-file`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      uploadDatasplittingJSONFile: builder.mutation<any, { file: File; workflowId: string }>({
        query: ({ file, workflowId }) => {
          const formData = new FormData();
          formData.append('file', file);

          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/datasplitting-json`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [],
      }),

      getAnnotators: builder.query<ListResponse<AnnotatorResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/annotators`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getSupervisors: builder.query<ListResponse<SupervisorResponse>, void>({
        query: () => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/supervisors`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getDataSplitSummary: builder.query<
        ObjectResponse<DataSplitSummaryResponse>,
        { workflowId: string }
      >({
        query: ({ workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/manage-projects/${workflowId}/data-split-summary`;
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),

      getAllocationSummary: builder.query<
        ListResponse<AllocationSummaryResponse>,
        { selectedAnnotators: AnnotationUser[]; defaultAllocationMode: boolean; workflowId: string }
      >({
        query: ({ selectedAnnotators, defaultAllocationMode, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/allocation-summary`,
            method: 'POST',
            body: {
              selectedAnnotators,
              defaultAllocationMode,
            },
          };
        },
        providesTags: [CREATE_PROJECT_WIZARD_TAG],
      }),
      uploadNotesS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/notes-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      uploadPersonS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/person-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      uploadVisitS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/visit-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      uploadDatasplittingJSONS3Path: builder.mutation<any, { s3Path: string; workflowId: string }>({
        query: ({ s3Path, workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/datasplitting-json-s3-path`,
            method: 'POST',
            body: { s3Path },
          };
        },
        invalidatesTags: [],
      }),

      getCreateProjectStatus: builder.query<
        ObjectResponse<{ status: string }> & { httpStatus: number },
        { workflowId: string }
      >({
        query: ({ workflowId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/manage-projects/${workflowId}/status`,
            responseHandler: async (response) => {
              const body = await response.json().catch(() => ({}));
              return { httpStatus: response.status, ...body };
            },
          };
        },
      }),
    }),
  });

export const {
  useGetManageProjectsQuery,
  useGetConditionAreasQuery,
  useGetOrganizationsQuery,
  useGetConceptGroupsQuery,
  useGetAttributesQuery,
  useCreateAnnotationProjectMutation,
  useCreateWorkflowIdMutation,
  useUploadNotesParquetFileMutation,
  useUploadPersonParquetFileMutation,
  useUploadVisitParquetFileMutation,
  useUploadDatasplittingJSONFileMutation,
  useGetAnnotatorsQuery,
  useGetSupervisorsQuery,
  useGetDataSplitSummaryQuery,
  useGetAllocationSummaryQuery,
  useGetMandatoryAttributeNamesQuery,
  useUploadNotesS3PathMutation,
  useUploadPersonS3PathMutation,
  useUploadVisitS3PathMutation,
  useUploadDatasplittingJSONS3PathMutation,
  useGetCreateProjectStatusQuery,
} = createProjectApiSlice;
